import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  Button,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarToday as DateRangeIcon,
  CheckCircle as StatusIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

export interface InvoiceFilters {
  statusFilter: string;
  dateRange?: { startDate: string; endDate: string };
}

type FilterCategory = 'dateRange' | 'invoiceStatus';

interface FilterPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  filters: InvoiceFilters;
  onFiltersChange: (filters: InvoiceFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

interface FilterCategoryConfig {
  id: FilterCategory;
  label: string;
  icon: React.ReactNode;
}

const filterCategories: FilterCategoryConfig[] = [
  { id: 'dateRange', label: 'Date range', icon: <DateRangeIcon /> },
  { id: 'invoiceStatus', label: 'Invoice Status', icon: <StatusIcon /> },
];

const INVOICE_STATUSES: Record<string, string> = {
  INITIATED: 'Initiated',
  PAID: 'Paid',
  HOLD: 'Hold',
  REJECTED: 'Rejected',
};

export const FilterPanel: React.FC<FilterPanelProps> = ({
  open,
  anchorEl,
  onClose,
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<FilterCategory>('invoiceStatus');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDateRangeType, setSelectedDateRangeType] = useState<string>('last7days');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  const handleCategorySelect = (category: FilterCategory) => {
    setSelectedCategory(category);
    setSearchTerm('');
  };

  const handleStatusChange = (status: string, checked: boolean) => {
    const currentStatuses = filters.statusFilter ? filters.statusFilter.split(',') : [];
    let newStatuses: string[];
    newStatuses = checked
      ? [...currentStatuses, status].filter(Boolean)
      : currentStatuses.filter((s) => s !== status);
    onFiltersChange({ ...filters, statusFilter: newStatuses.join(',') });
  };

  const getDateRangeForType = (rangeType: string): { startDate: string; endDate: string } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate: Date;
    let endDate: Date = new Date(today);
    endDate.setHours(23, 59, 59, 999);

    switch (rangeType) {
      case 'today':
        startDate = new Date(today);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'last7days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'last30days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 29);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        return { startDate: '', endDate: '' };
      default:
        return { startDate: '', endDate: '' };
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const handleDateRangeTypeChange = (rangeType: string) => {
    setSelectedDateRangeType(rangeType);
    if (rangeType === 'custom') {
      const todayStr = new Date().toISOString().split('T')[0];
      setCustomStartDate(customStartDate || todayStr);
      setCustomEndDate(customEndDate || todayStr);
    } else {
      const dateRange = getDateRangeForType(rangeType);
      onFiltersChange({
        ...filters,
        dateRange: dateRange.startDate
          ? { startDate: dateRange.startDate, endDate: dateRange.endDate }
          : undefined,
      });
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      const startDateTime = new Date(`${customStartDate}T00:00:00`);
      const endDateTime = new Date(`${customEndDate}T23:59:59`);
      onFiltersChange({
        ...filters,
        dateRange: {
          startDate: startDateTime.toISOString(),
          endDate: endDateTime.toISOString(),
        },
      });
    }
  };

  const isStatusSelected = (status: string) => {
    const currentStatuses = filters.statusFilter ? filters.statusFilter.split(',') : [];
    return currentStatuses.includes(status);
  };

  const renderFilterContent = () => {
    switch (selectedCategory) {
      case 'invoiceStatus':
        return (
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Invoice Status
              </Typography>
              <Button
                size="small"
                onClick={() => onFiltersChange({ ...filters, statusFilter: Object.keys(INVOICE_STATUSES).join(',') })}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Select all
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.entries(INVOICE_STATUSES)
                .filter(([key, label]) => !searchTerm || label.toLowerCase().includes(searchTerm.toLowerCase()))
                .map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={<Checkbox checked={isStatusSelected(key)} onChange={(e) => handleStatusChange(key, e.target.checked)} size="small" />}
                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Typography variant="body2">{label}</Typography></Box>}
                    sx={{ m: 0 }}
                  />
                ))}
            </Box>
          </Box>
        );
      case 'dateRange':
        return (
          <Box sx={{ p: 2 }}>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={selectedDateRangeType} onChange={(e) => handleDateRangeTypeChange(e.target.value)}>
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  label={<Box><Typography variant="body2">Custom</Typography></Box>}
                />
                {selectedCategory === 'dateRange' && selectedDateRangeType === 'custom' && (
                  <Box sx={{ ml: 4, mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <TextField
                        label="Start date"
                        type="date"
                        size="small"
                        value={customStartDate}
                        onChange={(e) => { setCustomStartDate(e.target.value); setTimeout(handleCustomDateChange, 0); }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1, minWidth: 150 }}
                      />
                      <TextField
                        label="End date"
                        type="date"
                        size="small"
                        value={customEndDate}
                        onChange={(e) => { setCustomEndDate(e.target.value); setTimeout(handleCustomDateChange, 0); }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1, minWidth: 150 }}
                      />
                    </Box>
                  </Box>
                )}
                <FormControlLabel value="today" control={<Radio />} label={<Box><Typography variant="body2">Today</Typography></Box>} />
                <FormControlLabel value="last7days" control={<Radio />} label={<Box><Typography variant="body2">Last 7 days</Typography></Box>} />
                <FormControlLabel value="last30days" control={<Radio />} label={<Box><Typography variant="body2">Last 30 days</Typography></Box>} />
              </RadioGroup>
            </FormControl>
          </Box>
        );
      default:
        return null;
    }
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.statusFilter) count += filters.statusFilter.split(',').filter(Boolean).length;
    if (filters.dateRange?.startDate && filters.dateRange?.endDate) count += 1;
    return count;
  };

  const [position, setPosition] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!open || !anchorEl) { setPosition({ top: 0, left: 0 }); return; }
    const updatePosition = () => {
      if (!anchorEl) { setPosition({ top: 0, left: 0 }); return; }
      const rect = anchorEl.getBoundingClientRect();
      setPosition({ top: rect.bottom + 8, left: rect.left });
    };
    updatePosition();
    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open, anchorEl]);

  if (!open) return null;

  return (
    <Paper elevation={8} sx={{ position: 'fixed', top: `${position.top}px`, left: `${position.left}px`, width: { xs: '70vw', sm: 800 }, maxHeight: '80vh', display: 'flex', flexDirection: 'column', zIndex: 1300, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: 240, borderRight: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflowY: 'auto' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">Filters</Typography>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Divider />
          <List sx={{ p: 0 }}>
            {filterCategories.map((category) => (
              <ListItem key={category.id} disablePadding>
                <ListItemButton
                  selected={selectedCategory === category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  sx={{ borderLeft: selectedCategory === category.id ? '3px solid' : '3px solid transparent', borderColor: selectedCategory === category.id ? 'primary.main' : 'transparent', bgcolor: selectedCategory === category.id ? 'action.selected' : 'transparent', '&:hover': { bgcolor: 'action.hover' }, py: 1.5, px: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box sx={{ color: selectedCategory === category.id ? 'primary.main' : 'text.secondary' }}>{category.icon}</Box>
                    <ListItemText primary={category.label} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: selectedCategory === category.id ? 600 : 400 }} />
                    {selectedCategory === category.id && (<ArrowForwardIcon fontSize="small" sx={{ color: 'primary.main' }} />)}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default', minHeight: 400, p: 2, pb: 8 }}>
          {renderFilterContent()}
        </Box>
      </Box>
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {getActiveFiltersCount() > 0 && (
            <Chip label={`${getActiveFiltersCount()} filter${getActiveFiltersCount() > 1 ? 's' : ''} applied`} size="small" onDelete={onClearFilters} color="primary" variant="outlined" />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={onClearFilters} variant="outlined">Clear</Button>
          <Button size="small" onClick={onApplyFilters} variant="contained" color="primary">Apply Filters</Button>
        </Box>
      </Box>
    </Paper>
  );
};

